import { z } from 'zod';
import { ShopperProps } from 'pages/Shoppers/LeadsRevival/LeadsRevivalTable/LeadsRevivalTableConfigs';
import { inventoryConditionSchema } from 'api/useInventory/inventorySchema';
import type { InventoryCondition } from 'types/InventoryItem';

export interface LeadsRevivalResponse {
  data: ShopperProps[];
}

function normalizeLastVehicleShopped(raw: unknown): unknown {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return raw;
  }
  const last = raw as Record<string, unknown>;
  const modelYearRaw = last.modelYear;
  const modelYear =
    typeof modelYearRaw === 'number' && Number.isFinite(modelYearRaw)
      ? modelYearRaw
      : Number(modelYearRaw);
  const ic = last.inventoryCondition;
  const inventoryCondition: InventoryCondition | undefined =
    ic === 'NEW' || ic === 'USED' || ic === 'CPO' ? ic : undefined;
  return {
    modelYear: Number.isFinite(modelYear) ? modelYear : 0,
    make: typeof last.make === 'string' ? last.make : String(last.make ?? ''),
    model: typeof last.model === 'string' ? last.model : '',
    inventoryCondition,
    vin: typeof last.vin === 'string' ? last.vin : String(last.vin ?? ''),
  };
}

/** idpPii often omits `name`, `address.address`, `city` / `state`, `lastVehicleShopped`, or nested keys. */
function normalizeShopperRow(raw: unknown): unknown {
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return raw;
  }
  const d = raw as Record<string, unknown>;

  const nameBlock =
    d.name !== null && typeof d.name === 'object' && !Array.isArray(d.name)
      ? (d.name as Record<string, unknown>)
      : {};

  const addressRaw = d.address;
  const addressObj =
    addressRaw !== null && typeof addressRaw === 'object' && !Array.isArray(addressRaw)
      ? (addressRaw as Record<string, unknown>)
      : {};

  // Revive shoppers are CRM leads, so every row should carry a lead id; accept either location.
  const leadIdFromName = typeof nameBlock.leadId === 'string' ? nameBlock.leadId : undefined;
  const leadIdFromRoot = typeof d.leadId === 'string' ? d.leadId : undefined;
  const leadId = leadIdFromName || leadIdFromRoot || undefined;

  return {
    notification: d.notification,
    name: {
      name: typeof nameBlock.name === 'string' ? nameBlock.name : String(nameBlock.name ?? ''),
      leadId,
    },
    email: typeof d.email === 'string' ? d.email : String(d.email ?? ''),
    address: {
      address: typeof addressObj.address === 'string' ? addressObj.address : '',
      postcode: typeof addressObj.postcode === 'string' ? addressObj.postcode : '',
    },
    city: typeof d.city === 'string' ? d.city : '',
    state: typeof d.state === 'string' ? d.state : '',
    lastVehicleShopped: normalizeLastVehicleShopped(d.lastVehicleShopped ?? {}),
    lostReason: typeof d.lostReason === 'string' ? d.lostReason : undefined,
    returnedDate: typeof d.returnedDate === 'string' ? d.returnedDate : '',
  };
}

const shopperPropsInnerSchema: z.ZodSchema<ShopperProps> = z.object({
  notification: z.boolean().optional(),
  name: z.object({
    name: z.string().optional(),
    leadId: z.string().optional(),
  }),
  email: z.string().optional(),
  address: z.object({
    address: z.string().optional(),
    postcode: z.string().optional(),
  }),
  city: z.string().optional(),
  state: z.string().optional(),
  lastVehicleShopped: z.object({
    modelYear: z.number().optional(),
    make: z.string().optional(),
    model: z.string().optional(),
    inventoryCondition: z.string().optional(),
    vin: z.string().optional(),
  }),
  lostReason: z.string().optional(),
  returnedDate: z.string(),
});

export const shopperPropsSchema: z.ZodSchema<ShopperProps> = z.preprocess(
  normalizeShopperRow,
  shopperPropsInnerSchema,
) as z.ZodSchema<ShopperProps>;

export const leadsRevivalSchema: z.ZodSchema<LeadsRevivalResponse> = z.object({
  data: z.array(shopperPropsSchema),
});
